import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import societyReviewService from '../../services/societyReviewService';
import { formatDate } from '../../utils/helpers';

const SocietyReviews = ({ societyId, societyName, onNavigateToReviews }) => {
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: ''
  });

  useEffect(() => {
    loadReviews();
    loadStats();
  }, [societyId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await societyReviewService.getSocietyReviews(societyId);
      setReviews(response.results || response);
      
      // Check if user has already reviewed
      if (isAuthenticated) {
        const existingReview = (response.results || response).find(review => review.user === user.id);
        if (existingReview) {
          setUserReview(existingReview);
          setReviewForm({
            rating: existingReview.rating,
            review_text: existingReview.review_text
          });
        }
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      showError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await societyReviewService.getSocietyReviewStats(societyId);
      setStats(stats);
    } catch (error) {
      console.error('Error loading review stats:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showError('Please log in to submit a review');
      return;
    }

    try {
      setSubmitting(true);
      
      if (userReview) {
        // Update existing review
        await societyReviewService.updateSocietyReview(societyId, userReview.id, reviewForm);
        showSuccess('Review updated successfully');
      } else {
        // Create new review
        await societyReviewService.createSocietyReview(societyId, reviewForm);
        showSuccess('Review submitted successfully');
      }
      
      setShowReviewForm(false);
      loadReviews();
      loadStats();
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError('Failed to submit review');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!userReview) return;
    
    if (window.confirm('Are you sure you want to delete your review?')) {
      try {
        await societyReviewService.deleteSocietyReview(societyId, userReview.id);
        showSuccess('Review deleted successfully');
        setUserReview(null);
        setReviewForm({ rating: 5, review_text: '' });
        loadReviews();
        loadStats();
      } catch (error) {
        console.error('Error deleting review:', error);
        showError('Failed to delete review');
      }
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={interactive && onChange ? () => onChange(star) : undefined}
            className={`text-2xl ${
              star <= rating
                ? 'text-yellow-400'
                : 'text-gray-300'
            } ${interactive ? 'hover:text-yellow-500 cursor-pointer' : ''}`}
            disabled={!interactive}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!stats?.rating_distribution) return null;

    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.rating_distribution[rating] || 0;
          const percentage = stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
          
          return (
            <div key={rating} className="flex items-center space-x-2">
              <span className="text-sm font-medium w-8">{rating}★</span>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 w-8">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Reviews & Ratings</h3>
        {isAuthenticated ? (
          <button
            onClick={() => {
              if (onNavigateToReviews) {
                onNavigateToReviews();
              }
              // Also show the form immediately when navigating to reviews tab
              setShowReviewForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {userReview ? 'Edit Review' : 'Write Review'}
          </button>
        ) : (
          <div className="text-sm text-gray-500">
            <a href="/login" className="text-blue-600 hover:text-blue-700">
              Login to write a review
            </a>
          </div>
        )}
      </div>

      {/* Review Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="flex items-center space-x-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {stats.average_rating.toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">out of 5</div>
              </div>
              <div>
                {renderStars(Math.round(stats.average_rating))}
                <div className="text-sm text-gray-600 mt-1">
                  {stats.total_reviews} review{stats.total_reviews !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Rating Distribution</h4>
            {renderRatingDistribution()}
          </div>
        </div>
      )}

      {/* Review Form */}
      {showReviewForm && isAuthenticated && (
        <div className="border-t pt-6 mb-6">
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              {renderStars(reviewForm.rating, true, (rating) => 
                setReviewForm({ ...reviewForm, rating })
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review (Optional)
              </label>
              <textarea
                value={reviewForm.review_text}
                onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Share your experience with this society..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Submitting...' : (userReview ? 'Update Review' : 'Submit Review')}
              </button>
              {userReview && (
                <button
                  type="button"
                  onClick={handleDeleteReview}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Review
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowReviewForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <div key={review.id} className="border-b border-gray-200 pb-4 last:border-b-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {review.user_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {review.user_name || 'Anonymous'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {review.created_at_display}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {renderStars(review.rating)}
                </div>
              </div>
              {review.review_text && (
                <p className="text-gray-700 mt-2">{review.review_text}</p>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">⭐</div>
            <p>No reviews yet. Be the first to review {societyName}!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocietyReviews;
